// Dica animada para gráficos de LINHA feitos em SVG — o mesmo comportamento da
// "Corrida do acumulado" da Linha do tempo das vendas (pedido do Vagner,
// 04/11/2026): ao passar o mouse, uma linha-guia vai para o ponto mais
// próximo, as bolinhas de cada série aparecem pulsando naquele ponto e uma
// caixa mostra os valores. A caixa desliza de um ponto para o outro, troca de
// lado perto da borda, fica embaixo quando as linhas estão no alto e some com
// fade. Funciona com o dedo (toque/arrasto). Quem pediu "menos movimento" no
// sistema operacional fica sem animação.
//
// Uso: envolva o <svg> do gráfico (largura 100%, viewBox "0 0 W H") e passe as
// MESMAS contas de posição do gráfico, no sistema de coordenadas do viewBox:
//   W, H        tamanho do viewBox
//   topo, base  faixa vertical da área do gráfico (altura da linha-guia)
//   n           quantos pontos há no eixo x (índices 0 … n-1)
//   xDe(i)      x do ponto i · yDe(v): y do valor v
//   series      [{ nome, cor, valor: i => número | null, forte, apagada }]
//               valor null = a série não tem ponto ali (sem bolinha, "—")
//               forte = bolinha maior e nome em negrito (ex.: o ano atual)
//               apagada = série esmaecida pela legenda (fica clarinha na caixa)
//   titulo(i)   cabeçalho da caixa (ex.: "MAR", "RPI 2913 · 03/11/2026")
//   formato(v)  como escrever o valor (use o formatador que respeita o olhinho)
//   rodape(i)   linha extra opcional no pé da caixa (ex.: comparação de anos)
//   ordenar     true = séries do maior para o menor valor naquele ponto
//
// DicaBarras (mais abaixo) é a versão para gráficos de BARRAS (2ª leva,
// 05/11/2026): acende a coluna sob o mouse e mostra a mesma caixa.
import React, { useState } from 'react';

const CSS = `
.dl-mov { transition: left .2s cubic-bezier(.2,.8,.2,1), top .2s cubic-bezier(.2,.8,.2,1), transform .2s cubic-bezier(.2,.8,.2,1), opacity .15s ease; }
.dl-faixa { transition: left .2s cubic-bezier(.2,.8,.2,1), width .2s cubic-bezier(.2,.8,.2,1), opacity .15s ease; }
.dl-valores { animation: dl-valor .22s ease-out; }
.dl-halo { animation: dl-pulso 1.4s ease-out infinite; }
@keyframes dl-valor { from { opacity: .25; transform: translateY(3px); } to { opacity: 1; transform: none; } }
@keyframes dl-pulso { from { transform: scale(1); opacity: .45; } to { transform: scale(2.6); opacity: 0; } }
@media (prefers-reduced-motion: reduce) {
  .dl-mov, .dl-faixa { transition: none; }
  .dl-valores, .dl-halo { animation: none; }
}`;

// Caixa escura com as linhas da dica (compartilhada pelas duas versões)
function CaixaDica({ i, cabecalho, linhas, extra }) {
  return (
    <div style={{ background: '#0a1628', color: '#fff', borderRadius: 8, padding: '8px 11px', boxShadow: '0 8px 22px rgba(10, 22, 40, 0.3)', fontSize: 11.5, fontVariantNumeric: 'tabular-nums', textAlign: 'left' }}>
      <div key={i} className="dl-valores">
        <div style={{ fontSize: 10, fontWeight: 800, color: '#94a3b8', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 5 }}>{cabecalho}</div>
        {linhas.map(s => (
          <div key={s.nome} style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 3, opacity: s.apagada ? 0.4 : 1 }}>
            <span style={{ width: 9, height: 9, flexShrink: 0, borderRadius: '50%', background: s.cor, boxShadow: '0 0 0 1.5px rgba(255, 255, 255, 0.75)' }} />
            <span style={{ color: '#e2e8f0', fontWeight: s.forte ? 800 : 600, minWidth: 0, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.nome}</span>
            <b style={{ flexShrink: 0, fontSize: 12.5 }}>{s.texto}</b>
          </div>
        ))}
        {extra && (
          <div style={{ borderTop: '1px solid rgba(148, 163, 184, 0.3)', marginTop: 5, paddingTop: 5, fontSize: 11, fontWeight: 700 }}>{extra}</div>
        )}
      </div>
    </div>
  );
}

const bolinha = (cor, r, halo) => (
  <>
    {halo && <span className="dl-halo" style={{ position: 'absolute', left: -r, top: -r, width: r * 2, height: r * 2, borderRadius: '50%', background: cor }} />}
    <span style={{ position: 'absolute', left: -r, top: -r, width: r * 2, height: r * 2, borderRadius: '50%', background: cor, boxShadow: '0 0 0 2px #fff' }} />
  </>
);

export default function DicaLinhas({ W, H, topo, base, n, xDe, yDe, series, titulo, formato, rodape, ordenar = false, largura = 240, children }) {
  const [foco, setFoco] = useState(null); // { i, w }: ponto sob o mouse e a largura do gráfico na tela
  const [ativo, setAtivo] = useState(false); // a caixa some com fade, por isso o foco fica guardado

  const focar = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    if (!r.width || !n) return;
    const xv = (e.clientX - r.left) / r.width * W;
    let i = 0, perto = Infinity;
    for (let k = 0; k < n; k++) {
      const d = Math.abs(xDe(k) - xv);
      if (d < perto) { perto = d; i = k; }
    }
    setFoco(f => (f && f.i === i && f.w === r.width ? f : { i, w: r.width }));
    setAtivo(true);
  };
  const soltar = (e) => { if (e.pointerType === 'mouse') setAtivo(false); };

  let camada = null;
  if (foco && foco.i < n) {
    const { i, w } = foco;
    const k = w / W; // o SVG mantém a proporção: a mesma escala vale para x e y
    const x = xDe(i) * k;
    const linhas = series.map(s => ({ ...s, v: s.valor(i) }));
    const comPonto = linhas.filter(s => s.v != null && Number.isFinite(s.v));
    const ys = comPonto.filter(s => !s.apagada).map(s => yDe(s.v));
    // pontos na metade de baixo → caixa no alto; pontos no alto → caixa embaixo
    const emBaixo = !ys.length || ys.reduce((a, b) => a + b, 0) / ys.length > (topo + base) / 2;
    const larg = Math.min(largura, w - 8);
    const left = x + 14 + larg <= w - 4 ? x + 14 : Math.max(4, x - 14 - larg);
    const lista = ordenar ? [...linhas].sort((a, b) => (b.v ?? -Infinity) - (a.v ?? -Infinity)) : linhas;
    const extra = rodape ? rodape(i) : null;
    camada = (
      <>
        <div className="dl-mov" style={{ position: 'absolute', left: x - 0.5, top: topo * k, height: (base - topo) * k, borderLeft: '1px dashed #64748b', opacity: ativo ? 1 : 0, pointerEvents: 'none' }} />
        {comPonto.map(s => (
          <div key={s.nome} className="dl-mov" style={{ position: 'absolute', left: x, top: yDe(s.v) * k, opacity: ativo ? (s.apagada ? 0.25 : 1) : 0, pointerEvents: 'none' }}>
            {bolinha(s.cor, s.forte ? 5.5 : 4, !s.apagada)}
          </div>
        ))}
        <div className="dl-mov" style={{
          position: 'absolute', left, top: (emBaixo ? topo : base) * k, width: larg, zIndex: 2, pointerEvents: 'none', opacity: ativo ? 1 : 0,
          transform: `translateY(${emBaixo ? 0 : -100}%) scale(${ativo ? 1 : 0.96})`, transformOrigin: emBaixo ? '50% 0' : '50% 100%',
        }}>
          <CaixaDica i={i} cabecalho={titulo(i)} extra={extra}
            linhas={lista.map(s => ({ ...s, texto: s.v == null ? '—' : formato(s.v) }))} />
        </div>
      </>
    );
  }

  return (
    <div style={{ position: 'relative', touchAction: 'pan-y' }} onPointerMove={focar} onPointerDown={focar} onPointerLeave={soltar}>
      <style>{CSS}</style>
      {children}
      {camada}
    </div>
  );
}

// ── Dica para gráficos de BARRAS ────────────────────────────────────────────
// Envolva o contêiner das barras e marque cada coluna (ou o grupo de barras do
// mesmo ponto) com data-col={i}. A coluna mais próxima do mouse acende e a
// caixa mostra o que titulo(i), linhas(i) e rodape(i) devolverem:
//   linhas(i)   [{ nome, cor, texto, forte }], com o texto já formatado
//               (use o formatador que respeita o olhinho)
// Serve para barras de <div> e para grupos <g> de SVG. A faixa e a caixa não
// pegam clique (pointer-events: none): o clique na coluna continua valendo.
export function DicaBarras({ titulo, linhas, rodape, largura = 240, children }) {
  const [foco, setFoco] = useState(null); // { i, left, width, w }: coluna sob o mouse
  const [ativo, setAtivo] = useState(false);

  const focar = (e) => {
    const caixa = e.currentTarget;
    const wr = caixa.getBoundingClientRect();
    if (!wr.width) return;
    let achou = null, perto = Infinity;
    for (const el of caixa.querySelectorAll('[data-col]')) {
      const r = el.getBoundingClientRect();
      const d = Math.abs(e.clientX - (r.left + r.width / 2));
      if (d < perto) { perto = d; achou = { i: Number(el.getAttribute('data-col')), left: r.left - wr.left, width: r.width }; }
    }
    if (!achou) return;
    achou.w = wr.width;
    setFoco(f => (f && f.i === achou.i && f.left === achou.left && f.width === achou.width && f.w === achou.w ? f : achou));
    setAtivo(true);
  };
  const soltar = (e) => { if (e.pointerType === 'mouse') setAtivo(false); };

  let camada = null;
  if (foco) {
    const { i, left, width, w } = foco;
    const larg = Math.min(largura, w - 8);
    const direita = left + width + 10;
    const xCaixa = direita + larg <= w - 4 ? direita : Math.max(4, left - 10 - larg);
    camada = (
      <>
        <div className="dl-faixa" style={{ position: 'absolute', left: left - 3, width: width + 6, top: 0, bottom: 0, borderRadius: 5, background: 'rgba(10, 22, 40, 0.08)', boxShadow: 'inset 0 0 0 1px rgba(10, 22, 40, 0.12)', opacity: ativo ? 1 : 0, pointerEvents: 'none' }} />
        <div className="dl-mov" style={{ position: 'absolute', left: xCaixa, top: 0, width: larg, zIndex: 2, pointerEvents: 'none', opacity: ativo ? 1 : 0, transform: `scale(${ativo ? 1 : 0.96})`, transformOrigin: '50% 0' }}>
          <CaixaDica i={i} cabecalho={titulo(i)} linhas={linhas(i) || []} extra={rodape ? rodape(i) : null} />
        </div>
      </>
    );
  }

  return (
    <div style={{ position: 'relative', touchAction: 'pan-y' }} onPointerMove={focar} onPointerDown={focar} onPointerLeave={soltar}>
      <style>{CSS}</style>
      {children}
      {camada}
    </div>
  );
}

// Variação de um valor contra o anterior para o rodapé da caixa
// (ex.: "vs RPI 2912: ↑ 5%"). menorMelhor = queda aparece em verde.
export function variacao(rotulo, a, b, menorMelhor = false) {
  if (a == null || b == null || !b) return null;
  const d = (a - b) / b;
  const bom = menorMelhor ? d <= 0 : d >= 0;
  return (
    <span style={{ color: d === 0 ? '#cbd5e1' : bom ? '#4ade80' : '#f87171' }}>
      {rotulo}: {d === 0 ? 'igual' : `${d > 0 ? '↑' : '↓'} ${Math.abs(Math.round(d * 100))}%`}
    </span>
  );
}

// Comparação de um ano com o anterior para o rodapé da caixa
// (ex.: "2026 × 2025: ↑ 12%"). menorMelhor = queda aparece em verde.
export function comparaAnos(anoA, a, anoB, b, menorMelhor = false) {
  return variacao(`${anoA} × ${anoB}`, a, b, menorMelhor);
}
